import { Injectable, signal } from '@angular/core';
import { UndoRedoAction } from '@models/undo-redo-action';

/**
 * Service to handle undo/redo functionality for the Study Expense Tracker.
 * Uses a stack-based approach to track changes and allow reverting/reapplying them.
 */
@Injectable({ providedIn: 'root' })
export class UndoRedoService {
    private readonly undoStack = signal<UndoRedoAction[]>([]);
    private readonly redoStack = signal<UndoRedoAction[]>([]);

    private readonly maxStackSize = 3;

    /**
     * Push a new action onto the undo stack.
     * This clears the redo stack as a new action branch is started.
     *
     * @param action The action to push onto the stack
     */
    pushAction(action: UndoRedoAction): void {
        const currentStack = this.undoStack();

        // Create new stack with the action added
        let newStack = [...currentStack, action];

        // Limit stack size
        if (newStack.length > this.maxStackSize) {
            newStack = newStack.slice(newStack.length - this.maxStackSize);
        }

        this.undoStack.set(newStack);

        // Clear redo stack when a new action is pushed
        this.redoStack.set([]);
    }

    /**
     * Undo the last action.
     *
     * @returns The action that was undone, or null if there's nothing to undo
     */
    undo(): UndoRedoAction | null {
        const currentUndoStack = this.undoStack();

        if (currentUndoStack.length === 0) {
            return null;
        }

        // Pop the last action from undo stack
        const action = currentUndoStack[currentUndoStack.length - 1];
        const newUndoStack = currentUndoStack.slice(0, -1);
        this.undoStack.set(newUndoStack);

        // Push to redo stack
        const currentRedoStack = this.redoStack();
        this.redoStack.set([...currentRedoStack, action]);

        return action;
    }

    /**
     * Redo the last undone action.
     *
     * @returns The action that was redone, or null if there's nothing to redo
     */
    redo(): UndoRedoAction | null {
        const currentRedoStack = this.redoStack();

        if (currentRedoStack.length === 0) {
            return null;
        }

        // Pop the last action from redo stack
        const action = currentRedoStack[currentRedoStack.length - 1];
        const newRedoStack = currentRedoStack.slice(0, -1);
        this.redoStack.set(newRedoStack);

        // Push back to undo stack
        const currentUndoStack = this.undoStack();
        this.undoStack.set([...currentUndoStack, action]);

        return action;
    }

    clear(): void {
        this.undoStack.set([]);
        this.redoStack.set([]);
    }
}
